'use client'

import Link from 'next/link'
import { useRef, useState } from 'react'
import { CaretLeft, CaretRight } from '@phosphor-icons/react'
import { listingShowsVerified } from '@/lib/listing-badges'
import VerifiedCheckBadge from '@/components/ui/VerifiedCheckBadge'

function getProfileSrc(listing: any) {
  if (listing?.user_type?.toLowerCase() === 'producer') {
    return listing?.photo || '/assets/icons/tractor.svg'
  }
  if (listing?.user_type?.toLowerCase() === 'market') {
    return listing?.photo || '/assets/icons/Market.svg'
  }
  return listing?.photo || '/assets/icons/tractor.svg'
}

function getListingHref(listing: any) {
  if (listing?.user_type?.toLowerCase() === 'market') return `/market/${listing?.slug}`
  return `/producer/${listing?.slug}`
}

function formatAddress(listing: any) {
  return [listing?.city].filter(Boolean).join(', ') || 'Local grower'
}

export const GrowingFarmers = ({ ingredient }: any) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showAll, setShowAll] = useState(false)
  const exactListings = Array.isArray(ingredient?.listings) ? ingredient.listings : []
  const parentName: string | null = ingredient?.parent_name ?? null
  const parentListings =
    parentName && Array.isArray(ingredient?.parent_listings) ? ingredient.parent_listings : []
  const childListings = Array.isArray(ingredient?.child_listings) ? ingredient.child_listings : []
  // Exact-variety growers first; parent-level growers follow, each labelled on its card.
  const listings = [
    ...exactListings.map((listing: any) => ({ ...listing, isParent: false })),
    ...parentListings.map((listing: any) => ({ ...listing, isParent: true })),
    ...childListings.map((listing: any) => ({ ...listing, isChild: true })),
  ]
  const hasListings = listings.length > 0
  const name = ingredient?.name || 'this ingredient'

  const scrollByCard = (dir: -1 | 1) => {
    const el = scrollRef.current
    if (!el) return
    const amount = Math.min(280, el.clientWidth * 0.8)
    el.scrollBy({ left: dir * amount, behavior: 'smooth' })
  }

  return (
    <div id="growing-farmers" className="flex scroll-mt-24 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-[#202020] sm:text-2xl sm:leading-[29px]">
          Where to find locally grown {name}
        </h2>
        <div className="flex items-center gap-4">
          {listings.length > 4 && (
            <button
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              className="inline-flex h-10 items-center justify-center rounded-full border border-[#85C349] px-6 text-sm font-medium text-[#6EAC36] transition hover:bg-[#F3FAEB]"
            >
              {showAll ? 'Show less' : 'View All'}
            </button>
          )}
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => scrollByCard(-1)}
            disabled={!hasListings || showAll}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#AEC3CD] text-[#202020] disabled:opacity-40"
          >
            <CaretLeft size={20} />
          </button>
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => scrollByCard(1)}
            disabled={!hasListings || showAll}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#AEC3CD] text-[#202020] disabled:opacity-40"
          >
            <CaretRight size={20} />
          </button>
        </div>
      </div>

      {!hasListings ? (
        <p className="rounded-xl border border-[#E6E6E6] bg-[#F7F7F7] px-4 py-8 text-center text-[#6B6B6B]">
          No farms or markets are currently listed for {name}.
        </p>
      ) : (
        <div
          ref={scrollRef}
          className={`flex gap-4 scroll-smooth ${showAll ? 'flex-wrap' : 'overflow-x-auto pb-2 [scrollbar-width:thin]'}`}
        >
          {listings.map((listing: any) => (
            <Link
              key={listing.listing_id}
              href={getListingHref(listing)}
              className="flex w-[248px] shrink-0 flex-col lg:w-[calc((100%-48px)/4)] items-center gap-3 rounded-xl border border-[#E6E6E6] bg-white p-4 transition-shadow hover:shadow-md"
            >
              <div className="relative h-[100px] w-[100px]">
                <img
                  src={getProfileSrc(listing)}
                  alt=""
                  className="h-[100px] w-[100px] rounded-full border border-[#E6E6E6] object-cover"
                />
              </div>
              <p className="w-full text-center text-lg font-semibold leading-[130%] tracking-[-0.01em] text-[#0D1000]">
                {listing.name}
              </p>
              <p className="w-full text-center text-sm leading-[150%] text-[#202020]">
                {formatAddress(listing)}
              </p>
              {listing.isChild ? (
                <p className="w-full text-center text-xs leading-[140%] text-[#6B6B6B]">
                  Grows varieties of {name}
                </p>
              ) : null}
              {listing.isParent ? (
                <p className="w-full text-center text-xs leading-[140%] text-[#6B6B6B]">
                  Grows {parentName} (varieties may include {name})
                </p>
              ) : null}
              {listingShowsVerified(listing) ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#DFEDFA] px-3 py-1 text-sm font-medium text-[#3978D7]">
                  Verified
                  <VerifiedCheckBadge size={18} />
                </span>
              ) : null}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
